import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import Login from "./pages/Login";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import OwnerDashboard from "./pages/OwnerDashboard";
import EngineerDashboard from "./pages/EngineerDashboard";
import EngineerReportsPage from "./pages/EngineerReportsPage";
import ContractorDashboard from "./pages/ContractorDashboard";
import SalesDashboard from "./pages/SalesDashboard";
import ManagementDashboard from "./pages/ManagementDashboard";
import AccountantDashboard from "./pages/AccountantDashboard";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDashboardPage from "./pages/ProjectDashboardPage";
import UnitsPage from "./pages/UnitsPage";
import TasksPage from "./pages/TasksPage";
import TeamTasksPage from "./pages/TeamTasksPage";
import ActivityLogsPage from "./pages/ActivityLogsPage";
import ProgressLogsV2Page from "./pages/ProgressLogsV2Page";
import ContractorProgressBoardPage from "./pages/ContractorProgressBoardPage";
import ReservationsPage from "./pages/ReservationsPage";
import BudgetManagementPage from "./pages/BudgetManagementPageV2";
import InvoiceManagementPage from "./pages/InvoiceManagementPageV2";
import CostDashboardPage from "./pages/CostDashboardPage";
import CostsPage from "./pages/CostsPage";
import CashFlowPage from "./pages/CashFlowPageV2";
import DocumentsHubPage from "./pages/DocumentsHubPage";
import UsersPage from "./pages/UsersPage";
import FinancesPage from "./pages/FinancesPage";
import OffersPage from "./pages/OffersPage";
import PartnersPage from "./pages/PartnersPage";
import SubcontractorsPage from "./pages/SubcontractorsPage";
import SubcontractorAccountingPage from "./pages/SubcontractorAccountingPage";
import LoansPage from "./pages/LoansPage";
import PotentialSalesPage from "./pages/PotentialSalesPage";
import RentalsPage from "./pages/RentalsPage";
import AssetsPage from "./pages/AssetsPage";
import ClientInquiriesPage from "./pages/ClientInquiriesPage";
import ProjectProgressPage from "./pages/ProjectProgressPage";
import JobSubtasksPage from "./pages/JobSubtasksPage";
import MyProgressLogPage from "./pages/MyProgressLogPage";
import MyOffersPage from "./pages/MyOffersPage";
import ContractorReportsPage from "./pages/ContractorReportsPage";
import ContractorInvoicesPage from "./pages/ContractorInvoicesPage";
import HRDashboard from "./pages/HRDashboard";
import EmployeesPage from "./pages/EmployeesPage";
import OtherExpensesPage from "./pages/OtherExpensesPage";
import PayrollPage from "./pages/PayrollPage";
import LawsuitsPage from "./pages/LawsuitsPage";
import AttorneyDashboard from './pages/AttorneyDashboard';
import { AttorneyRouteBoundary } from './components/AttorneyRouteBoundary';
import InvestmentOpportunitiesPage from "./pages/InvestmentOpportunitiesPage";
import PulsePlannerPage from "./pages/PulsePlannerPage";
import SettingsPage from "./pages/SettingsPage";
import UserSettingsPage from "./pages/UserSettingsPage";
import TeamTalkPage from "./pages/TeamTalkPage";
import JobMaterialsPage from "./pages/JobMaterialsPage";
import { Toaster } from "@/components/ui/sonner";
import DemoGateway from "./pages/DemoGateway";
import "@/App.css";

// Contractor Portal
import RequireContractor from "./portal/RequireContractor";
import PortalLayout from "./portal/PortalLayout";
import PortalTasks from "./portal/pages/Tasks";
import PortalLogs from "./portal/pages/Logs";
import PortalDocuments from "./portal/pages/Documents";
import PortalSettings from "./portal/pages/Settings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set axios defaults
axios.defaults.baseURL = API;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [demoMode, setDemoMode] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      axios
        .get("/auth/me")
        .then((res) => {
          setUser(res.data);
          setDemoMode(localStorage.getItem("demoMode") === "true");
        })
        .catch(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("demoMode");
          delete axios.defaults.headers.common["Authorization"];
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem("token", token);
    localStorage.removeItem("demoMode");
    setDemoMode(false);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
  };

  const handleDemoLogin = async () => {
    try {
      const res = await axios.get("/auth/demo");
      const { access_token, user: userData } = res.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("demoMode", "true");
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      setUser(userData);
      setDemoMode(true);
      return true;
    } catch {
      return false;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("demoMode");
    setDemoMode(false);
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-zinc-950">
        <div className="text-lg text-slate-600 dark:text-zinc-400">Loading...</div>
      </div>
    );
  }

  // Augment user with demoMode flag for all pages
  const userWithDemo = user ? { ...user, demoMode } : null;

  return (
    <div className="App">
      <Toaster position="top-right" />
      <BrowserRouter>
        <AttorneyRouteBoundary user={user}>
        <Routes>
          {/* Demo route - auto-login, then redirect to real route */}
          <Route path="/demo" element={<DemoGateway user={user} onDemoLogin={handleDemoLogin} />} />
          <Route path="/demo/*" element={<DemoGateway user={user} onDemoLogin={handleDemoLogin} />} />

          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : user.role === "owner" ? (
                <OwnerDashboard user={userWithDemo} onLogout={handleLogout} />
              ) : user.role === "management" ? (
                <ManagementDashboard user={userWithDemo} onLogout={handleLogout} />
              ) : user.role === "engineer" ? (
                <EngineerDashboard user={userWithDemo} onLogout={handleLogout} />
              ) : user.role === "contractor" ? (
                <ContractorDashboard user={userWithDemo} onLogout={handleLogout} />
              ) : user.role === "sales" ? (
                <SalesDashboard user={userWithDemo} onLogout={handleLogout} />
              ) : user.role === "accountant" ? (
                <AccountantDashboard user={userWithDemo} onLogout={handleLogout} />
              ) : user.role === "hr" ? (
                <HRDashboard user={userWithDemo} onLogout={handleLogout} />
              ) : user.role === "attorney" ? (
                <AttorneyDashboard user={userWithDemo} onLogout={handleLogout} />
              ) : (
                <div>Unknown role</div>
              )
            }
          />
          <Route
            path="/projects"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <ProjectsPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/projects/:projectId/dashboard"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <ProjectDashboardPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/units"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <UnitsPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/tasks"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <TasksPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/progress-logs"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <ProgressLogsV2Page user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/documents"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <DocumentsHubPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/reservations"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <ReservationsPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/finances"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <FinancesPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/offers"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <OffersPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/partners"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <PartnersPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/subcontractors"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : user.role === "accountant" ? (
                <SubcontractorAccountingPage user={userWithDemo} onLogout={handleLogout} />
              ) : (
                <SubcontractorsPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/loans"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <LoansPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/potential-sales"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <PotentialSalesPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/investments"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <InvestmentOpportunitiesPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/pulse-planner"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <PulsePlannerPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/rentals"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <RentalsPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route path="/assets" element={!user ? <Navigate to="/login" replace /> :
            ["owner", "management", "accountant"].includes(user.role) ?
              <AssetsPage user={userWithDemo} onLogout={handleLogout} /> : <Navigate to="/" replace />} />
          <Route
            path="/client-inquiries"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <ClientInquiriesPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/project-progress"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <ProjectProgressPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/project-progress/:projectId/job/:jobId"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <JobSubtasksPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/project-progress/:projectId/job/:jobId/materials"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <JobMaterialsPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/my-progress-log"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <ContractorProgressBoardPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/my-offers"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <MyOffersPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/contractor-reports"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <ContractorReportsPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/engineer-reports"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <EngineerReportsPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/contractor-invoices"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <ContractorInvoicesPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/budget"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <BudgetManagementPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/invoices"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <InvoiceManagementPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/cost-dashboard"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <CostDashboardPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/costs"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <CostsPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/cash-flow"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <CashFlowPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/team-tasks"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <TeamTasksPage user={userWithDemo} onLogout={handleLogout} />
              )
            }
          />
          <Route
            path="/activity-logs"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : user.role === "owner" ? (
                <ActivityLogsPage user={userWithDemo} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/users"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : user.role === "owner" ? (
                <UsersPage user={userWithDemo} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/lawsuits"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : ["owner", "management", "attorney"].includes(user.role) ? (
                <LawsuitsPage user={userWithDemo} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* HR Manager Routes */}
          <Route
            path="/employees"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : ["hr", "owner", "management"].includes(user.role) ? (
                <EmployeesPage user={userWithDemo} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/other-expenses"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : ["hr", "owner", "management", "accountant"].includes(user.role) ? (
                <OtherExpensesPage user={userWithDemo} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/payroll"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : ["hr", "owner", "management", "accountant"].includes(user.role) ? (
                <PayrollPage user={userWithDemo} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/settings"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : ["owner", "management"].includes(user.role) ? (
                <SettingsPage user={userWithDemo} onLogout={handleLogout} onUserUpdate={(updatedUser) => setUser(prev => ({ ...prev, ...updatedUser }))} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/my-settings"
            element={
              !user ? <Navigate to="/login" replace /> :
              <UserSettingsPage user={userWithDemo} onLogout={handleLogout} onUserUpdate={(u) => setUser(prev => ({ ...prev, ...u }))} />
            }
          />

          <Route
            path="/messages"
            element={
              !user ? <Navigate to="/login" replace /> :
              <TeamTalkPage user={userWithDemo} onLogout={handleLogout} />
            }
          />



          {/* Contractor Portal (RBAC enforced) */}
          <Route element={<RequireContractor user={userWithDemo} /> }>
            <Route path="/portal" element={<PortalLayout user={userWithDemo} onLogout={handleLogout} /> }>
              <Route index element={<Navigate to="/portal/tasks" replace />} />
              <Route path="tasks" element={<PortalTasks user={userWithDemo} />} />
              <Route path="logs" element={<PortalLogs user={userWithDemo} />} />
              <Route path="documents" element={<PortalDocuments user={userWithDemo} />} />
              <Route path="settings" element={<PortalSettings onLogout={handleLogout} />} />
            </Route>
          </Route>
        </Routes>
        </AttorneyRouteBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
